import defaultLanguage from "../../locales/en.json";
import type { SupportedLanguage, TranslationKeys } from "@/types/services/locale";

// Re-export types for backward compatibility
export type { SupportedLanguage, TranslationKeys };

class I18nService {
  private currentLanguage: SupportedLanguage = "en";
  private translations: Record<SupportedLanguage, TranslationKeys> =
    {} as Record<SupportedLanguage, TranslationKeys>;
  private loadedLanguages: Set<SupportedLanguage> = new Set();
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.loadLanguageFromStorage();
  }

  private loadLanguageFromStorage(): void {
    const stored = localStorage.getItem("selectedLanguage");
    if (stored && this.isValidLanguage(stored)) {
      this.currentLanguage = stored as SupportedLanguage;
    }
  }

  private isValidLanguage(lang: string): lang is SupportedLanguage {
    const supportedLanguages: SupportedLanguage[] = [
      "bg",
      "da",
      "de",
      "en",
      "es",
      "et",
      "fi",
      "fr",
      "it",
      "lt",
      "lv",
      "nl",
      "no",
      "pl",
      "pt",
      "ru",
      "sv",
    ];
    return supportedLanguages.includes(lang as SupportedLanguage);
  }

  private async loadSpecificLanguage(
    language: SupportedLanguage
  ): Promise<void> {
    // Don't load if already loaded
    if (this.loadedLanguages.has(language)) {
      return;
    }

    // Map language codes to their translation modules
    // Note: 'en' is statically imported at the top, so we use it directly
    const languageModuleMap: Record<SupportedLanguage, () => Promise<any>> = {
      en: async () => ({ default: defaultLanguage }),
      da: () => import("../../locales/da.json"),
      de: () => import("../../locales/de.json"),
      es: () => import("../../locales/es.json"),
      et: () => import("../../locales/et.json"),
      fi: () => import("../../locales/fi.json"),
      fr: () => import("../../locales/fr.json"),
      it: () => import("../../locales/it.json"),
      lt: () => import("../../locales/lt.json"),
      lv: () => import("../../locales/lv.json"),
      nl: () => import("../../locales/nl.json"),
      no: () => import("../../locales/no.json"),
      pl: () => import("../../locales/pl.json"),
      pt: () => import("../../locales/pt.json"),
      ru: () => import("../../locales/ru.json"),
      sv: () => import("../../locales/sv.json"),
      bg: () => import("../../locales/bg.json"),
    };

    const moduleLoader = languageModuleMap[language];
    if (!moduleLoader) {
      throw new Error(`No loader found for language: ${language}`);
    }

    const translationModule = await moduleLoader();
    this.translations[language] = translationModule.default as TranslationKeys;
    this.loadedLanguages.add(language);
  }

  async loadTranslations(): Promise<void> {
    await this.loadSpecificLanguage(this.currentLanguage);
    this.notifyListeners();
  }

  async setLanguage(language: SupportedLanguage): Promise<void> {
    if (!this.loadedLanguages.has(language)) {
      await this.loadSpecificLanguage(language);
    }

    this.currentLanguage = language;
    localStorage.setItem("selectedLanguage", language);
    this.notifyListeners();
  }

  getCurrentLanguage(): SupportedLanguage {
    return this.currentLanguage;
  }

  t<K extends keyof TranslationKeys>(key: K): TranslationKeys[K] {
    const translation = this.translations[this.currentLanguage];
    if (!translation) return defaultLanguage[key] as TranslationKeys[K];
    return (translation[key] ?? defaultLanguage[key]) as TranslationKeys[K];
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }
}

export const i18n = new I18nService();
export default i18n;
